import {IClientStorageService} from "../../Contracts/GloablServices/IClientStorageService";

// Characters js-cookie leaves unencoded in cookie values (see js-cookie v2 write path).
const VALUE_ALLOWED_ENCODED_CHARS = /%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g;
const KEY_ALLOWED_ENCODED_CHARS = /%(23|24|26|2B|5E|60|7C)/g;
const ENCODED_SEQUENCE = /(%[0-9A-Z]{2})+/g;

const encodeValue = (value: string): string =>
    encodeURIComponent(value).replace(VALUE_ALLOWED_ENCODED_CHARS, decodeURIComponent);

const encodeKey = (key: string): string =>
    encodeURIComponent(key).replace(KEY_ALLOWED_ENCODED_CHARS, decodeURIComponent);

const decode = (raw: string): string => raw.replace(ENCODED_SEQUENCE, decodeURIComponent);

// A malformed percent sequence written by another script must not break lookups of unrelated keys.
const safeDecode = (raw: string): string | null => {
    try {
        return decode(raw);
    } catch (e: unknown) {
        return null;
    }
};

/**
 * Cookie backed storage scoped to the current host (no Domain attribute) and the whole site (Path=/).
 * The wire format is intentionally compatible with js-cookie v2, which the legacy widgets use to
 * read and write the same cookies.
 */
export class CookieStorageService implements IClientStorageService {
    constructor(private readonly maxAgeSeconds: number) {
    }

    set(key: string, value: string): void {
        const expires = new Date(Date.now() + this.maxAgeSeconds * 1000);
        document.cookie = [
            `${encodeKey(key)}=${encodeValue(String(value))}`,
            `expires=${expires.toUTCString()}`,
            'path=/',
            'SameSite=Lax',
        ].join('; ');
    }

    get(key: string): string | null {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        for (const cookie of cookies) {
            const separatorIndex = cookie.indexOf('=');
            if (separatorIndex === -1) {
                continue;
            }
            const name = safeDecode(cookie.slice(0, separatorIndex));
            if (name === null || name !== key) {
                continue;
            }
            let rawValue = cookie.slice(separatorIndex + 1);
            if (rawValue.charAt(0) === '"') {
                rawValue = rawValue.slice(1, -1);
            }
            try {
                return decode(rawValue);
            } catch (e: unknown) {
                return rawValue;
            }
        }
        return null;
    }

    delete(key: string): void {
        document.cookie = `${encodeKey(key)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
}
